import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { authClient, useAuth, formatApiError } from "@/lib/auth";
import { toast } from "sonner";
import { sfx } from "@/lib/sounds";
import { reportEvent } from "@/lib/api";
import Countdown from "@/components/Countdown";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  Calendar, MapPin, Trophy, Users, ShieldCheck, ArrowLeft, ExternalLink,
  Ticket, X as XIcon, Clock, ListChecks, CheckCircle2, AlertCircle,
  Share2, Flag, Image as ImageIcon, Info, Hourglass,
} from "lucide-react";

const client = () => authClient;

export default function EventDetail() {
  const { slug } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [ev, setEv] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setErr(null);
    try {
      const { data } = await client().get(`/events/${slug}`);
      setEv(data);
    } catch (e) {
      console.warn("event detail failed", e?.message);
      setErr(e?.response?.status === 404 ? "not_found" : "error");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug]);
  useEffect(() => {
    if (ev?.title) {
      document.title = `${ev.title} · LostDC Events`;
    }
    return () => { document.title = "LostDC Network"; };
  }, [ev?.title]);

  const doRegister = async () => {
    if (!user) return toast.error("Please sign in to register");
    setBusy(true);
    try {
      const { data } = await client().post(`/events/${slug}/register`);
      if (data.status === "waitlisted") {
        sfx.no();
        toast.success("Added to the waitlist", { description: "We'll notify you if a spot opens." });
      } else if (data.status === "pending") {
        sfx.pling();
        toast.success("Registration submitted", { description: "Awaiting organizer approval." });
      } else if (data.status === "already_registered") {
        toast.info("You're already registered.");
      } else {
        sfx.levelup();
        toast.success("Registered!", { description: `See you at ${ev.title}` });
      }
      await load();
    } catch (e) {
      sfx.error();
      toast.error(formatApiError(e));
    }
    setBusy(false);
  };
  const doCancel = async () => {
    if (!window.confirm("Cancel your registration?")) return;
    setBusy(true);
    try {
      await client().delete(`/events/${slug}/register`);
      sfx.no();
      toast.success("Registration cancelled");
      await load();
    } catch (e) { sfx.error(); toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const doShare = async () => {
    const url = `${window.location.origin}/events/${slug}`;
    const shareData = { title: ev?.title || "LostDC Event", text: ev?.description || "", url };
    try {
      if (navigator.share) { await navigator.share(shareData); return; }
      await navigator.clipboard.writeText(url);
      toast.success("Event link copied");
    } catch { /* user cancelled share */ }
  };

  const doReport = async () => {
    if (!user) return toast.error("Please sign in to report");
    const reason = window.prompt("Report reason (spam, inappropriate, misleading, other):", "spam");
    if (!reason) return;
    const details = window.prompt("Additional details (optional):", "") || "";
    try {
      await reportEvent(slug, { reason, details });
      toast.success("Report submitted — thank you.");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (err === "not_found") {
    return (
      <div className="pt-40 pb-24 px-4 max-w-2xl mx-auto text-center" data-testid="event-not-found">
        <div className="rounded-2xl glass p-10">
          <AlertCircle className="mx-auto mb-3 text-red-400" />
          <div className="font-hero text-3xl">Event not found</div>
          <div className="text-sm text-white/50 mt-2">Slug <span className="text-[#FFBF00]">{slug}</span> doesn't exist.</div>
          <button onClick={() => nav("/events")} className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#FFBF00] hover:bg-[#FF8C00] text-black text-xs uppercase tracking-widest font-semibold">
            <ArrowLeft size={12} /> All Events
          </button>
        </div>
      </div>
    );
  }
  if (err) return <div className="pt-40 text-center text-red-400" data-testid="event-error">Failed to load event.</div>;
  if (!ev) return <div className="pt-40 text-center text-white/50" data-testid="event-loading">Loading event…</div>;

  const isFull = ev.max_participants && ev.registered_count >= ev.max_participants;

  return (
    <div className="pb-24" data-testid={`page-event-${ev.slug}`}>
      {/* HERO */}
      <section className="relative min-h-[60vh] flex items-end overflow-hidden pt-24 pb-14">
        <div className="absolute inset-0">
          {ev.image ? <img src={ev.image} alt="" className="w-full h-full object-cover" /> : (
            <div className="w-full h-full bg-gradient-to-br from-[#FFBF00]/10 via-black to-[#FF8C00]/10" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-transparent" />
        </div>
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 w-full">
          <Link to="/events" className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-white/60 hover:text-white mb-3">
            <ArrowLeft size={12} /> All Events
          </Link>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#FFBF00]">{ev.status || "Live Event"}</div>
          <h1 className="mt-2 font-hero text-5xl sm:text-6xl md:text-7xl leading-[0.9]">{ev.title}</h1>
          {ev.description && <p className="mt-3 text-lg text-white/70 max-w-3xl">{ev.description}</p>}
          <div className="mt-6 flex flex-wrap items-center gap-3">
            {user && ev.is_pending ? (
              <button onClick={doCancel} disabled={busy} data-testid="event-pending-btn"
                      className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-yellow-400/40 hover:bg-yellow-400/10 text-yellow-300 text-xs uppercase tracking-widest">
                <Hourglass size={14} /> Pending Approval — Cancel
              </button>
            ) : user && ev.is_registered ? (
              <button onClick={doCancel} disabled={busy} data-testid="event-cancel-btn"
                      className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-red-400/40 hover:bg-red-400/10 text-red-300 text-xs uppercase tracking-widest font-semibold">
                <XIcon size={14} /> Cancel Registration
              </button>
            ) : user && ev.is_waitlisted ? (
              <button onClick={doCancel} disabled={busy} data-testid="event-leave-waitlist"
                      className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-white/20 hover:border-red-400/40 text-white/80 text-xs uppercase tracking-widest">
                <Clock size={14} /> On Waitlist — Leave
              </button>
            ) : (
              <button onClick={doRegister} disabled={busy} data-testid="event-register-btn"
                      className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#FFBF00] hover:bg-[#FF8C00] text-black text-xs uppercase tracking-widest font-semibold disabled:opacity-60">
                <Ticket size={14} /> {isFull ? "Join Waitlist" : (ev.requires_approval ? "Request to Join" : "Register Now")}
              </button>
            )}
            <div className="inline-flex items-center gap-2 px-4 py-3 rounded-full glass" data-testid="event-counter">
              <Users size={14} className="text-[#FFBF00]" />
              <span className="font-mono text-sm">
                {ev.registered_count || 0}{ev.max_participants ? ` / ${ev.max_participants}` : ""} registered
              </span>
              {ev.waitlist_count > 0 && <span className="text-xs text-white/40">· {ev.waitlist_count} waitlist</span>}
              {ev.pending_count > 0 && <span className="text-xs text-yellow-400/70">· {ev.pending_count} pending</span>}
            </div>
            <button onClick={doShare} data-testid="event-share-btn"
                    className="inline-flex items-center gap-2 px-4 py-3 rounded-full glass hover:border-[#FFBF00]/40">
              <Share2 size={14} /> <span className="text-xs uppercase tracking-widest">Share</span>
            </button>
            {user && (
              <button onClick={doReport} data-testid="event-report-btn"
                      className="inline-flex items-center gap-2 px-4 py-3 rounded-full glass hover:border-red-400/40 text-white/60">
                <Flag size={13} /> <span className="text-xs uppercase tracking-widest">Report</span>
              </button>
            )}
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 mt-10 grid lg:grid-cols-[1fr_320px] gap-6">
        {/* MAIN */}
        <div className="space-y-6">
          <div className="rounded-2xl glass p-6">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#FFBF00] mb-2">Countdown</div>
            <Countdown target={ev.starts_at} />
          </div>

          {ev.content_md && (
            <div className="rounded-2xl glass p-6" data-testid="event-content-md">
              <div className="flex items-center gap-2 mb-3">
                <Info size={14} className="text-[#FFBF00]" />
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#FFBF00]">About This Event</div>
              </div>
              <div className="prose prose-invert max-w-none text-sm text-white/80 event-markdown">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{ev.content_md}</ReactMarkdown>
              </div>
            </div>
          )}

          {ev.rewards?.length > 0 && (
            <div className="rounded-2xl glass p-6">
              <div className="flex items-center gap-2 mb-3">
                <Trophy size={14} className="text-[#FFBF00]" />
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#FFBF00]">Prize Pool</div>
              </div>
              <div className="flex flex-wrap gap-2">
                {ev.rewards.map((r) => (
                  <span key={r} className="text-sm px-3 py-1.5 rounded-full border border-[#FFBF00]/40 bg-[#FFBF00]/10 text-[#FFBF00]">
                    {r}
                  </span>
                ))}
              </div>
            </div>
          )}

          {ev.rules?.length > 0 && (
            <div className="rounded-2xl glass p-6">
              <div className="flex items-center gap-2 mb-3">
                <ListChecks size={14} className="text-[#FFBF00]" />
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#FFBF00]">Rules & Requirements</div>
              </div>
              <ul className="space-y-2 text-sm text-white/80 list-disc list-inside">
                {ev.rules.map((r, i) => <li key={`rule-${i}`}>{r}</li>)}
              </ul>
            </div>
          )}

          {ev.requirements?.length > 0 && (
            <div className="rounded-2xl glass p-6" data-testid="event-requirements">
              <div className="flex items-center gap-2 mb-3">
                <ShieldCheck size={14} className="text-[#FFBF00]" />
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#FFBF00]">Requirements</div>
              </div>
              <ul className="space-y-2 text-sm text-white/80 list-disc list-inside">
                {ev.requirements.map((r, i) => <li key={`req-${i}`}>{r}</li>)}
              </ul>
            </div>
          )}

          {ev.gallery?.length > 0 && (
            <div className="rounded-2xl glass p-6" data-testid="event-gallery">
              <div className="flex items-center gap-2 mb-3">
                <ImageIcon size={14} className="text-[#FFBF00]" />
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#FFBF00]">Gallery</div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {ev.gallery.map((src, i) => (
                  <a key={`g-${i}`} href={src} target="_blank" rel="noreferrer"
                     className="block aspect-square rounded-lg overflow-hidden glass hover:border-[#FFBF00]/40">
                    <img src={src} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform" />
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Participants */}
          <div className="rounded-2xl glass p-6">
            <div className="flex items-center gap-2 mb-3">
              <Users size={14} className="text-[#FFBF00]" />
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#FFBF00]">Registered Participants</div>
            </div>
            {ev.participants?.length === 0 ? (
              <div className="text-sm text-white/40 py-6 text-center">Be the first to register!</div>
            ) : (
              <div className="flex flex-wrap gap-2" data-testid="event-participants">
                {ev.participants.map((p) => (
                  <Link key={p.id} to={`/community/${p.id}`}
                        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass hover:border-[#FFBF00]/40 ${p.waitlisted ? "opacity-60" : ""}`}
                        title={p.waitlisted ? "On waitlist" : "Registered"}>
                    {p.avatar && <img src={p.avatar} alt="" className="w-5 h-5 rounded" />}
                    <span className="text-xs">{p.mc_username || p.name}</span>
                    {p.waitlisted && <Clock size={10} className="text-yellow-400" />}
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* SIDEBAR */}
        <aside className="space-y-4 lg:sticky lg:top-24 self-start">
          <div className="rounded-2xl glass p-5 space-y-2 text-sm">
            <div className="flex items-center gap-2 text-white/80">
              <Calendar size={13} className="text-[#FFBF00]" />
              {ev.starts_at ? new Date(ev.starts_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "TBA"}
            </div>
            {ev.location && (
              <div className="flex items-center gap-2 text-white/80">
                <MapPin size={13} className="text-[#FFBF00]" /> {ev.location}
              </div>
            )}
            {ev.organizer && (
              <div className="flex items-center gap-2 text-white/80">
                <ShieldCheck size={13} className="text-[#FFBF00]" /> {ev.organizer}
              </div>
            )}
            {ev.registration_deadline && (
              <div className="flex items-center gap-2 text-white/60 text-xs">
                <Clock size={12} /> Register by {new Date(ev.registration_deadline).toLocaleDateString("en-IN")}
              </div>
            )}
          </div>
          {ev.discord_url && (
            <a href={ev.discord_url} target="_blank" rel="noreferrer"
               className="block rounded-2xl glass p-4 hover:border-[#5865F2]/40">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-[#5865F2]">Discord</div>
                  <div className="text-sm">Join the event chat</div>
                </div>
                <ExternalLink size={14} className="text-white/50" />
              </div>
            </a>
          )}
          {ev.mc_server && (
            <div className="rounded-2xl glass p-4">
              <div className="text-[10px] uppercase tracking-widest text-[#FFBF00]">Minecraft Server</div>
              <div className="mt-1 font-mono text-sm">{ev.mc_server}</div>
            </div>
          )}
          {user && (ev.is_registered || ev.is_waitlisted || ev.is_pending) && (
            <div className={`rounded-2xl glass p-4 border ${ev.is_pending ? "border-yellow-400/30" : "border-green-400/30"}`} data-testid="event-registration-status">
              <div className={`flex items-center gap-2 text-sm font-bold ${ev.is_pending ? "text-yellow-300" : "text-green-400"}`}>
                <CheckCircle2 size={14} />
                {ev.is_pending ? "Awaiting approval"
                  : ev.is_registered ? "You're registered"
                  : "You're on the waitlist"}
              </div>
              <div className="text-xs text-white/60 mt-1">
                {ev.is_pending ? "The organizer will approve or decline your request soon."
                  : "We'll notify you before the event starts."}
              </div>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
